/**
 * Faanuu — Pure JS game engine. Mirrors backend/game_engine.py.
 * Board: 5x5 intersections (25 nodes, 4x4 cells with diagonals).
 * Coordinates: (x, y) with (0,0) bottom-left, (4,4) top-right.
 * board[y][x] = null | { owner: 'A'|'B', king: boolean }
 */

export const SIZE = 5;

export const DIRS_A = [[0, 1], [1, 0], [-1, 0], [1, 1], [-1, 1]];
export const DIRS_B = [[0, -1], [1, 0], [-1, 0], [1, -1], [-1, -1]];
export const DIRS_KING = [
  [0, 1], [0, -1], [1, 0], [-1, 0],
  [1, 1], [-1, 1], [1, -1], [-1, -1],
];

const PROMO_A = new Set([`0,${SIZE - 1}`, `${SIZE - 1},${SIZE - 1}`]);
const PROMO_B = new Set(["0,0", `${SIZE - 1},0`]);

export function initialBoard() {
  const board = Array.from({ length: SIZE }, () => Array(SIZE).fill(null));
  for (let x = 0; x < SIZE; x++) {
    board[0][x] = { owner: "A", king: false };
    board[1][x] = { owner: "A", king: false };
    board[SIZE - 2][x] = { owner: "B", king: false };
    board[SIZE - 1][x] = { owner: "B", king: false };
  }
  return board;
}

/** Validate a custom 5x5 board layout. Returns the board if valid, null otherwise. */
export function validateBoard(b) {
  if (!Array.isArray(b) || b.length !== SIZE) return null;
  for (let y = 0; y < SIZE; y++) {
    if (!Array.isArray(b[y]) || b[y].length !== SIZE) return null;
    for (let x = 0; x < SIZE; x++) {
      const c = b[y][x];
      if (c === null) continue;
      if (typeof c !== "object" || (c.owner !== "A" && c.owner !== "B") || typeof c.king !== "boolean") return null;
    }
  }
  return b;
}

const CUSTOM_KEY = "faanuu.customSetup.v1";

/** Read a saved custom layout from localStorage, or null if not present/invalid. */
export function loadCustomSetup() {
  try {
    const raw = typeof window !== "undefined" && window.localStorage.getItem(CUSTOM_KEY);
    if (!raw) return null;
    return validateBoard(JSON.parse(raw));
  } catch (_) {
    return null;
  }
}

export function saveCustomSetup(b) {
  if (typeof window === "undefined") return;
  if (!b) {
    window.localStorage.removeItem(CUSTOM_KEY);
    return;
  }
  window.localStorage.setItem(CUSTOM_KEY, JSON.stringify(b));
}

export function getInitialBoardForPlay() {
  return loadCustomSetup() || initialBoard();
}

export function cloneBoard(b) {
  return b.map((row) => row.map((c) => (c ? { ...c } : null)));
}

export function inBounds(x, y) {
  return x >= 0 && x < SIZE && y >= 0 && y < SIZE;
}

export function pieceDirs(piece) {
  if (piece.king) return DIRS_KING;
  return piece.owner === "A" ? DIRS_A : DIRS_B;
}

/** Build the full intersection path between two aligned cells (for king slides). */
export function buildSlidePath(from, to) {
  const [fx, fy] = from;
  const [tx, ty] = to;
  const dx = Math.sign(tx - fx);
  const dy = Math.sign(ty - fy);
  if (dx === 0 && dy === 0) return [[fx, fy]];
  const path = [[fx, fy]];
  let cx = fx + dx;
  let cy = fy + dy;
  while (cx !== tx || cy !== ty) {
    path.push([cx, cy]);
    cx += dx;
    cy += dy;
  }
  path.push([tx, ty]);
  return path;
}

export function getCapturesForPiece(board, x, y) {
  const piece = board[y][x];
  if (!piece) return [];
  const out = [];
  for (const [dx, dy] of pieceDirs(piece)) {
    if (piece.king) {
      let cx = x + dx;
      let cy = y + dy;
      while (inBounds(cx, cy) && !board[cy][cx]) {
        cx += dx;
        cy += dy;
      }
      if (!inBounds(cx, cy)) continue;
      const enemy = board[cy][cx];
      if (!enemy || enemy.owner === piece.owner) continue;
      if (enemy.king && !piece.king) continue;
      const over = [cx, cy];
      let lx = cx + dx;
      let ly = cy + dy;
      while (inBounds(lx, ly) && !board[ly][lx]) {
        out.push({ to: [lx, ly], over });
        lx += dx;
        ly += dy;
      }
      continue;
    }

    const mx = x + dx;
    const my = y + dy;
    const tx = x + 2 * dx;
    const ty = y + 2 * dy;
    if (!inBounds(mx, my) || !inBounds(tx, ty)) continue;
    const mid = board[my][mx];
    const target = board[ty][tx];
    if (!mid || target) continue;
    if (mid.owner === piece.owner) continue;
    if (mid.king && !piece.king) continue;
    out.push({ to: [tx, ty], over: [mx, my] });
  }
  return out;
}

export function getSimpleMovesForPiece(board, x, y) {
  const piece = board[y][x];
  if (!piece) return [];
  const out = [];
  for (const [dx, dy] of pieceDirs(piece)) {
    if (piece.king) {
      let nx = x + dx;
      let ny = y + dy;
      while (inBounds(nx, ny) && !board[ny][nx]) {
        out.push([nx, ny]);
        nx += dx;
        ny += dy;
      }
      continue;
    }
    const nx = x + dx;
    const ny = y + dy;
    if (inBounds(nx, ny) && !board[ny][nx]) out.push([nx, ny]);
  }
  return out;
}

export function playerHasCapture(board, owner) {
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const p = board[y][x];
      if (p && p.owner === owner && getCapturesForPiece(board, x, y).length) return true;
    }
  }
  return false;
}

function enumerateCaptureChains(board, sx, sy) {
  const piece = board[sy][sx];
  if (!piece) return [];
  const results = [];

  const dfs = (curBoard, cx, cy, path, captured) => {
    const caps = getCapturesForPiece(curBoard, cx, cy);
    if (!caps.length) {
      if (captured.length) {
        results.push({ path: path.slice(), captured: captured.slice() });
      }
      return;
    }
    for (const cap of caps) {
      const [tx, ty] = cap.to;
      const [mx, my] = cap.over;
      const newBoard = cloneBoard(curBoard);
      const moving = newBoard[cy][cx];
      newBoard[cy][cx] = null;
      newBoard[my][mx] = null;
      newBoard[ty][tx] = { owner: moving.owner, king: moving.king };
      path.push([tx, ty]);
      captured.push([mx, my]);
      dfs(newBoard, tx, ty, path, captured);
      path.pop();
      captured.pop();
    }
  };

  dfs(cloneBoard(board), sx, sy, [[sx, sy]], []);
  return results;
}

/**
 * Return all legal moves for owner.
 * Each move: { from:[x,y], to:[x,y], captures:[[x,y],...], path:[[x,y],...] }
 */
export function getLegalMoves(board, owner) {
  const mustCapture = playerHasCapture(board, owner);
  const moves = [];
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const p = board[y][x];
      if (!p || p.owner !== owner) continue;
      if (mustCapture) {
        const chains = enumerateCaptureChains(board, x, y);
        for (const chain of chains) {
          moves.push({
            from: [x, y],
            to: chain.path[chain.path.length - 1],
            captures: chain.captured,
            path: chain.path,
          });
        }
      } else {
        for (const t of getSimpleMovesForPiece(board, x, y)) {
          moves.push({
            from: [x, y],
            to: t,
            captures: [],
            path: p.king ? buildSlidePath([x, y], t) : [[x, y], t],
          });
        }
      }
    }
  }
  return moves;
}

export function legalMovesFrom(board, x, y) {
  const piece = board[y][x];
  if (!piece) return [];
  const all = getLegalMoves(board, piece.owner);
  return all.filter((m) => m.from[0] === x && m.from[1] === y);
}

export function applyMove(board, move) {
  const newBoard = cloneBoard(board);
  const [fx, fy] = move.from;
  const [tx, ty] = move.to;
  const piece = newBoard[fy][fx];
  newBoard[fy][fx] = null;
  for (const [cx, cy] of move.captures || []) newBoard[cy][cx] = null;
  let king = piece.king;
  if (!king) {
    const k = `${tx},${ty}`;
    if (piece.owner === "A" && PROMO_A.has(k)) king = true;
    if (piece.owner === "B" && PROMO_B.has(k)) king = true;
  }
  newBoard[ty][tx] = { owner: piece.owner, king };
  return newBoard;
}

export function checkWinner(board, nextPlayer) {
  let a = 0, b = 0;
  for (const row of board) for (const c of row) if (c) c.owner === "A" ? a++ : b++;
  if (a === 0) return "B";
  if (b === 0) return "A";
  if (getLegalMoves(board, nextPlayer).length === 0) return nextPlayer === "A" ? "B" : "A";
  return null;
}

export function countPieces(board) {
  let a = 0, b = 0, ka = 0, kb = 0;
  for (const row of board)
    for (const c of row) {
      if (!c) continue;
      if (c.owner === "A") { a++; if (c.king) ka++; }
      else { b++; if (c.king) kb++; }
    }
  return { a, b, ka, kb };
}
